import clsx from 'clsx';
import { forwardRef } from 'react';
import { FormError } from '~/components/Form/Error/FormError';
import styles from './RadioButtonGroup.module.css';
import type { RadioGroupProps, RadioProps } from './RadioButtonGroup.types';

const RadioButtonGroup = forwardRef<
  HTMLDivElement,
  RadioGroupProps & {
    errorMessage?: string;
  }
>(({ ariaLabel, children, required, title, variant = 'default', titleVariant = 'default', id, errorMessage }, ref) => {
  const groupStyle = clsx({
    [styles.groupList]: variant === 'list',
    [styles.groupDefault]: variant === 'default'
  });
  const labelId = id ? `${id}-label` : undefined;
  return (
    <div
      role="radiogroup"
      data-test-id="RadioGroup"
      className={groupStyle}
      aria-required={required}
      ref={ref}
      aria-label={ariaLabel}
      aria-labelledby={title ? labelId : undefined}
      id={id}
    >
      {title && (
        <span id={labelId} className={clsx(styles.legend, { [styles.legendLarge]: titleVariant === 'large' })}>
          {title}
          {required && (
            <span aria-hidden="true" className={styles.requiredIndicator}>
              *
            </span>
          )}
        </span>
      )}
      {children}

      {errorMessage ? <FormError message={errorMessage} /> : null}
    </div>
  );
});

const RadioButton = forwardRef<HTMLInputElement, RadioProps>(
  ({ checked, children, disabled, id, name, value, tabIndex = 0, ...props }, ref) => {
    const labelStyle = clsx(styles.label, {
      [styles.labelDisabled]: disabled === true
    });
    return (
      <label htmlFor={id} className={labelStyle}>
        <input
          data-test-id={`${id}-RadioButton`}
          name={name}
          id={id}
          type="radio"
          className={styles.input}
          checked={checked}
          disabled={disabled}
          ref={ref}
          value={value}
          {...props}
          tabIndex={tabIndex}
        />
        <span className={styles.radio}></span>
        <span className={styles.labelText}>{children}</span>
      </label>
    );
  }
);

RadioButton.displayName = 'RadioButton';
RadioButtonGroup.displayName = 'RadioButtonGroup';

export { RadioButton, RadioButtonGroup };
